import React, { createContext, useContext } from 'react';
import { useColorPalette } from '../hooks/useColorPalette';

// Creiamo un contesto React per i temi
const ThemeContext = createContext();

/**
 * ThemeProvider fornisce un contesto per accedere facilmente ai colori del tema in tutta l'applicazione
 * Avvolge i componenti figli e rende disponibili i colori attraverso useTheme hook
 */
export const ThemeProvider = ({ children }) => {
  // Utilizziamo l'hook useColorPalette per ottenere la palette colori
  const colorPalette = useColorPalette();
  
  return (
    <ThemeContext.Provider value={colorPalette}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook personalizzato per accedere al tema e ai colori
 * Può essere usato in qualsiasi componente figlio di ThemeProvider
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme deve essere utilizzato all\'interno di un ThemeProvider');
  }
  return context;
};

/**
 * Componente per applicare colori di sfondo e testo in base al tema
 * Esempio: <ThemedBox colorType="primary">Contenuto</ThemedBox>
 */
export const ThemedBox = ({ 
  children, 
  colorType = 'primary', 
  variant = 'filled', 
  className = '', 
  style = {},
  ...props 
}) => {
  const theme = useTheme();
  
  // Determina i colori in base al tipo e alla variante
  let bgColor, textColor, borderColor;
  
  // Gestisce varianti comuni
  if (variant === 'filled') {
    if (colorType === 'primary') {
      bgColor = theme.primary;
      textColor = theme.textOnPrimary;
      borderColor = 'transparent';
    } else if (colorType === 'secondary') {
      bgColor = theme.secondary;
      textColor = theme.textOnSecondary;
      borderColor = 'transparent';
    } else if (colorType === 'accent') {
      bgColor = theme.accent;
      textColor = theme.textOnAccent;
      borderColor = 'transparent';
    } else if (colorType.startsWith('button-')) {
      // Per bottoni: button-primary, button-secondary, ecc.
      const buttonType = colorType.replace('button-', '');
      if (theme.buttons && theme.buttons[buttonType]) {
        bgColor = theme.buttons[buttonType].background;
        textColor = theme.buttons[buttonType].text;
        borderColor = theme.buttons[buttonType].border;
      }
    } else if (colorType.startsWith('badge-')) {
      // Per badge: badge-primary, badge-warning, ecc.
      const badgeType = colorType.replace('badge-', '');
      if (theme.badge && theme.badge[badgeType]) {
        bgColor = theme.badge[badgeType].background;
        textColor = theme.badge[badgeType].text;
      }
    }
  } else if (variant === 'outlined') {
    bgColor = 'transparent';
    if (colorType === 'primary') {
      borderColor = theme.primary;
      textColor = theme.primary;
    } else if (colorType === 'secondary') {
      borderColor = theme.secondary;
      textColor = theme.secondary;
    } else if (colorType.startsWith('button-')) {
      const buttonType = colorType.replace('button-', '');
      if (theme.buttons && theme.buttons[buttonType]) {
        borderColor = theme.buttons[buttonType].background;
        textColor = theme.buttons[buttonType].background;
      }
    }
  } else if (variant === 'text') {
    bgColor = 'transparent';
    borderColor = 'transparent';
    if (theme.text && theme.text[colorType]) {
      textColor = theme.text[colorType];
    } else if (colorType === 'primary') {
      textColor = theme.primary;
    } else if (colorType === 'secondary') {
      textColor = theme.secondary;
    }
  }

  // Applica i colori come stili inline
  const combinedStyle = {
    backgroundColor: bgColor,
    color: textColor,
    borderColor: borderColor,
    ...style
  };

  return (
    <div className={className} style={combinedStyle} {...props}>
      {children}
    </div>
  );
};

/**
 * Componente per applicare colori di testo in base al tema
 * Esempio: <ThemedText colorType="primary">Testo</ThemedText>
 */
export const ThemedText = ({ children, colorType = 'primary', className = '', style = {}, ...props }) => {
  const theme = useTheme();
  
  let textColor;
  
  // Determina il colore del testo
  if (theme.text && theme.text[colorType]) {
    textColor = theme.text[colorType];
  } else if (colorType === 'primary') {
    textColor = theme.primary;
  } else if (colorType === 'secondary') {
    textColor = theme.secondary;
  } else if (colorType === 'accent') {
    textColor = theme.accent;
  } else if (colorType.startsWith('icon-')) {
    const iconType = colorType.replace('icon-', '');
    if (theme.icons && theme.icons[iconType]) {
      textColor = theme.icons[iconType];
    }
  }
  
  const combinedStyle = {
    color: textColor,
    ...style
  };
  
  return (
    <span className={className} style={combinedStyle} {...props}>
      {children}
    </span>
  );
};

/**
 * Componente per applicare colori alle icone
 * Esempio: <ThemedIcon iconType="primary" className="fa-solid fa-user" />
 */
export const ThemedIcon = ({ iconType = 'primary', className = '', style = {}, ...props }) => {
  const theme = useTheme();
  
  let iconColor;
  
  // Determina il colore dell'icona
  if (theme.icons && theme.icons[iconType]) {
    iconColor = theme.icons[iconType];
  } else if (iconType === 'primary') {
    iconColor = theme.icons?.primary || theme.primary;
  } else if (iconType === 'secondary') {
    iconColor = theme.icons?.secondary || theme.secondary;
  }
  
  const combinedStyle = {
    color: iconColor,
    ...style
  };
  
  return (
    <i className={className} style={combinedStyle} {...props}></i>
  );
};

/**
 * Componente per creare bottoni con il tema
 * Esempio: <ThemedButton buttonType="primary">Clic</ThemedButton>
 */
export const ThemedButton = ({ 
  children, 
  buttonType = 'primary', 
  variant = 'filled',
  className = '', 
  style = {},
  disabled = false,
  ...props 
}) => {
  const theme = useTheme();
  
  let bgColor, textColor, borderColor, hoverBgColor;
  const actualType = disabled ? 'disabled' : buttonType;
  
  // Determina i colori in base al tipo e alla variante
  if (variant === 'filled') {
    if (theme.buttons && theme.buttons[actualType]) {
      bgColor = theme.buttons[actualType].background;
      textColor = theme.buttons[actualType].text;
      hoverBgColor = theme.buttons[actualType].hover;
      borderColor = theme.buttons[actualType].border || 'transparent';
    } else {
      // Fallback
      bgColor = actualType === 'primary' ? theme.primary : 
                actualType === 'secondary' ? theme.secondary : '#c5c5c5';
      textColor = getTextColorForBackground(bgColor);
      hoverBgColor = darkenColor(bgColor, 10);
      borderColor = 'transparent';
    }
  } else if (variant === 'outlined') {
    bgColor = 'transparent';
    if (theme.buttons && theme.buttons.outline) {
      textColor = theme.buttons.outline.text || (actualType === 'primary' ? theme.primary : theme.secondary);
      borderColor = theme.buttons.outline.border || textColor;
      hoverBgColor = theme.buttons.outline.hover || '#f0f0f0';
    } else {
      textColor = actualType === 'primary' ? theme.primary : theme.secondary;
      borderColor = textColor;
      hoverBgColor = '#f0f0f0';
    }
  }

  // Stile base del bottone
  const baseButtonStyle = {
    padding: '0.5rem 1rem',
    borderRadius: '0.25rem',
    border: '1px solid',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.2s ease-in-out',
    backgroundColor: bgColor,
    color: textColor,
    borderColor: borderColor,
    ...style
  };
  
  return (
    <button 
      className={className}
      style={baseButtonStyle}
      disabled={disabled}
      onMouseOver={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = hoverBgColor;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = bgColor;
      }}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * Funzioni di supporto
 */
function getTextColorForBackground(hexColor) {
  if (!hexColor || typeof hexColor !== 'string' || hexColor.length < 6) return '#000000';
  
  const hex = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;
  
  let r, g, b;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }
  
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  return luminance >= 128 ? '#000000' : '#FFFFFF';
}

function darkenColor(hexColor, percent) {
  if (!hexColor || typeof hexColor !== 'string' || hexColor.length < 6) return '#000000';
  
  let hex = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;
  
  let r, g, b;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }
  
  const amount = Math.floor(255 * (percent / 100));
  
  r = Math.max(0, r - amount);
  g = Math.max(0, g - amount);
  b = Math.max(0, b - amount);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
